import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Dumbbell, Heart, Activity, Target, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const methodDetails = {
  "intelligent-strength": {
    title: "Intelligent Strength",
    intro: "Strategic strength programming designed specifically for women 35+ to build lean muscle, protect metabolism, and increase resilience.",
    emphasis: "This is not random intensity.",
    subtitle: "Your training is built around:",
    bullets: [
      "Building lean muscle safely and strategically",
      "Protecting joints and improving movement quality",
      "Matching training intensity to your recovery capacity",
      "Nutrition that supports energy and body composition",
      "Recovery strategies that improve consistency and results"
    ],
    body: "We assess your energy, stress load, injury history, and lifestyle before building your program.\n\nYou will train hard — but intelligently.",
    resultTitle: "The result?",
    results: [
      "Stronger legs.",
      "Defined muscle.",
      "Stable energy.",
      "Improved recovery.",
      "A body that responds again."
    ],
    closing: "This is intelligent, personalized strength training for women who want to age with confidence, vitality, and resilience for decades to come."
  },
  "hrv-recovery": {
    title: "HRV-Guided Recovery",
    intro: "Training adjustments driven by real-time biometric data.",
    emphasis: "Your body does not need more stress.\nIt needs the right stress — at the right time.",
    subtitle: "You will:",
    bullets: [
      "Match your training to your body's readiness",
      "Avoid overtraining and metabolic shutdown",
      "Improve sleep and energy stability",
      "Build resilience without depletion"
    ],
    body: "Using HRV and recovery metrics, we determine when your body is ready to push and when it needs restoration. This helps improve energy, protect the nervous system, and prevent burnout that often stalls progress.",
    resultTitle: "Recovery is not rest.",
    results: ["It is strategy."],
    closing: "This is precision-based longevity training — guided by your data, not guesswork."
  },
  "macronutrient-strategy": {
    title: "Macronutrient Strategy",
    intro: "Personalized nutrition designed to support lean muscle, stable energy, and sustainable body composition.",
    emphasis: "This is not restrictive dieting.\n\nAfter 35, your metabolism responds to precision — not deprivation.",
    subtitle: "We focus on:",
    bullets: [
      "Strategic protein intake to protect muscle",
      "Carbohydrate timing to support hormones and recovery",
      "Balanced fueling to prevent cortisol-driven fat storage",
      "Metabolic flexibility without extreme restriction"
    ],
    body: "You will track intentionally — not obsessively.\n\nYou will fuel strength — not starve it.",
    resultTitle: "The result?",
    results: [
      "Improved body composition.",
      "Stable energy.",
      "Stronger recovery.",
      "A metabolism that works with you again."
    ],
    closing: "Nutrition becomes a tool for longevity — not a source of stress."
  },
  "nervous-system": {
    title: "Nervous System Regulation",
    intro: "Optimize your stress response to unlock true recovery and longevity.",
    emphasis: "After 35, unmanaged stress can quietly stall fat loss, muscle growth, and energy stability.\n\nThis is where precision matters.",
    subtitle: "You will:",
    bullets: [
      "Train in alignment with your recovery capacity",
      "Reduce cortisol-driven inflammation",
      "Improve sleep quality and depth",
      "Stabilize energy throughout the day",
      "Build resilience without burnout"
    ],
    body: "Chronic stress can keep your body stuck in survival mode, making body composition, recovery, energy, and performance harder than they need to be and can lead to diminishing returns. We help restore balance so your body can respond, adapt, and thrive.",
    resultTitle: "Strength without regulation is stress.",
    results: ["When your nervous system is balanced, your body responds."],
    closing: "This is longevity training at its foundation."
  }
};

export const MethodSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedMethod, setSelectedMethod] = useState(null);

  const methods = [
    {
      id: "intelligent-strength",
      icon: Dumbbell,
      title: "Intelligent Strength",
      description: "Strategic programming designed for women 35+ to build lean muscle and metabolic resilience.",
      span: "md:col-span-7 md:row-span-2",
      large: true
    },
    {
      id: "hrv-recovery",
      icon: Activity,
      title: "HRV-Guided Recovery",
      description: "Training adjustments based on real-time biometric data.",
      span: "md:col-span-5",
      large: false
    },
    {
      id: "macronutrient-strategy",
      icon: Target,
      title: "Macronutrient Strategy",
      description: "Personalized nutrition for sustainable body composition.",
      span: "md:col-span-5",
      large: false
    },
    {
      id: "nervous-system",
      icon: Heart,
      title: "Nervous System Regulation",
      description: "Optimize recovery and stress response for longevity.",
      span: "md:col-span-12",
      wide: true
    }
  ];

  return (
    <section 
      id="method"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12 bg-sand/20"
      data-testid="method-section"
    >
      <div className="max-w-screen-2xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-[#F5F0EA]/60 mb-4">
            The Approach
          </p>
          <h2 
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight uppercase text-[#F5F0EA]"
            data-testid="method-title"
          >
            The Method
          </h2>
        </motion.div>

        {/* Tetris Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          {methods.map((method, index) => (
            <motion.div
              key={method.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={`${method.span} group cursor-pointer`}
              onClick={() => setSelectedMethod(method.id)}
            >
              <div 
                className={`h-full bg-[#F5F0EA] border border-[#EAE5DD] hover:border-sand-dark hover:shadow-lg transition-all duration-500 p-8 md:p-10 flex flex-col ${
                  method.large ? "min-h-[400px] md:min-h-[500px]" : "min-h-[200px]"
                } ${method.wide ? "md:flex-row md:items-center md:gap-12" : ""}`}
                data-testid={`method-card-${index}`}
              >
                {/* Icon */}
                <div className={`mb-6 ${method.wide ? "md:mb-0" : ""}`}>
                  <method.icon 
                    className="text-sand-dark group-hover:scale-110 transition-transform duration-300" 
                    size={method.large ? 48 : 36} 
                    strokeWidth={1}
                  />
                </div>

                {/* Content */}
                <div className={method.large ? "mt-auto" : ""}>
                  <h3 className="font-heading text-xl md:text-2xl tracking-wide uppercase text-graphite-dark mb-3 group-hover:text-sand-dark transition-colors">
                    {method.title}
                  </h3>
                  <p className={`font-body font-light text-graphite/60 ${
                    method.large ? "text-base md:text-lg max-w-md" : "text-sm md:text-base"
                  }`}>
                    {method.description}
                  </p>
                  <p className="font-body text-xs tracking-widest uppercase text-sand-dark mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to learn more →
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Method Detail Modal */}
      <Dialog open={!!selectedMethod} onOpenChange={() => setSelectedMethod(null)}>
        <DialogContent 
          className="bg-[#F5F0EA] border-none max-w-2xl max-h-[85vh] overflow-y-auto p-0"
          data-testid="method-modal"
        >
          {selectedMethod && methodDetails[selectedMethod] && (
            <>
              <DialogHeader className="sticky top-0 bg-[#F5F0EA] p-8 pb-4 border-b border-sand/30 z-10">
                <DialogTitle className="font-heading text-2xl md:text-3xl tracking-wide uppercase text-graphite-dark">
                  {methodDetails[selectedMethod].title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Learn more about {methodDetails[selectedMethod].title}
                </DialogDescription>
              </DialogHeader>
              
              <div className="p-8 pt-6 space-y-6">
                {/* Intro */}
                <p className="font-body text-base md:text-lg text-graphite/80 leading-relaxed">
                  {methodDetails[selectedMethod].intro}
                </p>

                {/* Emphasis */}
                <p className="font-accent text-lg md:text-xl italic text-graphite-dark whitespace-pre-line">
                  {methodDetails[selectedMethod].emphasis}
                </p>

                {/* Subtitle + Bullets */}
                <div>
                  <p className="font-body text-sm tracking-widest uppercase text-sand-dark mb-4">
                    {methodDetails[selectedMethod].subtitle}
                  </p>
                  <ul className="space-y-2">
                    {methodDetails[selectedMethod].bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-sand-dark mt-1.5">•</span>
                        <span className="font-body text-graphite/70">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Body */}
                <p className="font-body text-graphite/70 leading-relaxed whitespace-pre-line">
                  {methodDetails[selectedMethod].body}
                </p>

                {/* Results */}
                <div className="bg-sand/20 p-6 border-l-2 border-sand-dark">
                  <p className="font-heading text-lg uppercase text-graphite-dark mb-3">
                    {methodDetails[selectedMethod].resultTitle}
                  </p>
                  <div className="space-y-1">
                    {methodDetails[selectedMethod].results.map((result, i) => (
                      <p key={i} className="font-body text-graphite/80">{result}</p>
                    ))}
                  </div>
                </div>

                {/* Closing */}
                <p className="font-accent text-lg italic text-graphite-dark border-t border-sand/30 pt-6">
                  {methodDetails[selectedMethod].closing}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MethodSection;
